import { LanguagePopover } from '@/components/LanguagePopover/LanguagePopover'
import { commonUtils } from '@/helpers/commonUtils'
import { InputNumber, Popover, Tooltip } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import style from './MiddleStatus.module.less'

import { ModelPopover } from '@/components/ModelPopover/ModelPopover'
import { SearchPopover } from '@/components/SearchPopover /SearchPopover'
import { TranslateSpeedSettingPopover } from '@/components/TranslateSpeedSettingPopover/TranslateSpeedSetting'
import { UpgradeLinkBtn } from '@/components/UpgradeLinkBtn/UpgradeLinkBtn'
import { VipBadge } from '@/components/VipBadge/VipBadge'
import { estimateTranslateTime } from '@/pages/PdfUltra/utils/fileUtils'
import { TranslateServiceType } from '@/services/translateService'
import { TranslateServiceConsts } from '@/services/translateService/consts'
import { userService } from '@/services/userService'

type MiddleStatusProps = {
  totalPage: number
  file: File
  styleDefaultValue: TranslateServiceType.StyleType
  glossaryMode: TranslateServiceType.GlossaryMode
  onBack: () => void
  onTranslateClick: (params: {
    start: number
    end: number
    targetLang: string
    model: ServerDataTypes.Model
    contextType: PostDataTypes.ContextType
    glossaryMode: TranslateServiceType.GlossaryMode
  }) => void
  onStyleChange: (style: TranslateServiceType.StyleType) => void
  onGlossaryChange: (glossary: TranslateServiceType.GlossaryMode) => void
}

export const MiddleStatus = observer((props: MiddleStatusProps) => {
  const {
    onBack,
    totalPage,
    onTranslateClick,
    file,
    styleDefaultValue,
    onStyleChange,
    onGlossaryChange,
    glossaryMode,
  } = props

  const [error, setError] = useState<boolean>(false)
  const [startPage, setStartPage] = useState<number | null>(1)
  const [endPage, setEndPage] = useState<number | null>(totalPage)
  const [info, setInfo] = useState<string>('')
  const [upgradeBtnText, setUpgradeBtnText] = useState<string | null>(null)
  const [buyPackageBtnText, setBuyPackageBtnText] = useState<string | null>(null)
  const [parseMode, setParseMode] = useState<TranslateServiceType.ParseSpeedMode>('low')
  const [contextType, setContextType] = useState<PostDataTypes.ContextType>('close')

  useEffect(() => {
    pageChanged(startPage, endPage)

    let contextType: PostDataTypes.ContextType = 'close'
    if (userService.vipVersion === 'ultra') {
      contextType = 'full'
    } else if (userService.vipVersion === 'max') {
      contextType = 'deep'
    }
    setContextType(contextType)
    userService.savePdfProContextType(contextType)
  }, [])

  useEffect(() => {
    const userVersion = userService.vipVersion
    if (userVersion === 'free') {
      setParseMode('low')
    } else if (userVersion === 'ultra') {
      setParseMode('high')
    } else {
      setParseMode('medium')
    }
  }, [])

  function translateNow() {
    if (error) return
    const result = pageChanged(startPage, endPage)
    if (result) {
      console.log(startPage, endPage)
      onTranslateClick({
        start: startPage! - 1,
        end: endPage! - 1,
        targetLang: userService.pdfProTargetLang,
        model: userService.pdfProTranslateModel,
        contextType: userService.pdfProContextType,
        glossaryMode: userService.pdfProGlossaryMode,
      })
    }
  }

  function pageChanged(start: number | null, end: number | null) {
    console.log(start, end)
    setUpgradeBtnText(null)
    setBuyPackageBtnText(null)

    if (start === null || end === null) {
      setError(true)
      setInfo('请输入页码')
      return false
    }

    // 检查最小值和最大值
    if (start < 1 || end < 1) {
      setError(true)
      setInfo('页码不能小于1')
      return false
    }

    if (start > totalPage || end > totalPage) {
      setError(true)
      setInfo('起始页或结束页不能大于总页数')
      return false
    }

    // 确保起始页不大于结束页
    if (start > end) {
      setError(true)
      setInfo('起始页不能大于结束页')
      return false
    }

    const total = end - start + 1

    const maxPageRule = userService.getPlanFileMaxRule('pdf_pro')
    if (typeof maxPageRule.maxParsePage === 'number' && total > maxPageRule.maxParsePage) {
      setError(true)
      setInfo(
        `${userService.vipVersionName}单次翻译最大支持 ${maxPageRule.maxParsePage} 页，请调整页数范围`
      )
      setUpgradeBtnText('升级解锁更大页数限制')
      return false
    }

    const canUseRes = userService.checkPdfPageCount('pdf_pro', total)
    if (!canUseRes.allowed) {
      setError(true)
      setInfo(`翻译页数不足。剩余 ${canUseRes.remaining ?? 0} 页，上传的页数是 ${total} 页`)
      setBuyPackageBtnText('获取更多页数')
      setUpgradeBtnText('升级解锁更大套餐额度')
      return
    }

    // 使用高级模型时，检查token是否足够
    if (userService.checkTokenCountByModel(userService.pdfProTranslateModel) <= 0) {
      setError(true)
      setInfo('模型 Token 不足，请升级会员或切换为基础模型')
      setBuyPackageBtnText('获取更多 Token')
      setUpgradeBtnText('升级解锁更大套餐额度')
      return false
    }

    // 文件最大 100M
    const fileSize = file.size / 1024 / 1024
    const maxFileSize = maxPageRule.fileSize
    if (fileSize > maxFileSize) {
      setError(true)
      setInfo(
        `${userService.vipVersionName}最大支持 ${maxFileSize}M 的文件，请调整文件大小或升级会员`
      )
      setUpgradeBtnText('升级解锁更大文件大小限制')
      return false
    }

    setError(false)
    setInfo(`*共选中翻译${total}页`)
    setUpgradeBtnText(null)
    setBuyPackageBtnText(null)
    return true
  }

  const fileSize =
    file.size / 1024 / 1024 >= 1
      ? `${(file.size / 1024 / 1024).toFixed(0)}MB`
      : `${(file.size / 1024).toFixed(0)}KB`

  return (
    <div className={style.middleStatus}>
      <div className={classNames(style.back)} onClick={onBack}>
        <img className={style.icon} src={commonUtils.getStaticFile('pdf_back.png')} />
        <span className={style.text}>返回</span>
      </div>
      <div className={classNames(style.content)}>
        <div className={classNames(style.left)}>
          <div className={classNames(style.singleBox)}>
            <div className={classNames(style.fileInfo)}>
              <img
                className={classNames(style.pdfIcon)}
                src={commonUtils.getStaticFile('pdf_icon.png')}
              />
              <div className={classNames(style.wrap)}>
                <p className={classNames(style.pdfName)}>
                  <Tooltip title={file.name}>{file.name}</Tooltip>
                </p>
                <p className={classNames(style.pdfSize)}>{fileSize}</p>
              </div>
            </div>
            <div className={classNames(style.countInfo)}>
              <div className={classNames(style.countBox)}>
                <p className={classNames(style.countText)}>共 {totalPage} 页。</p>
                <p className={classNames(style.countText)}>
                  全部翻译预计用时{' '}
                  <Popover
                    content={
                      <div style={{ maxWidth: '380px' }}>
                        <p>翻译用时 = 文件总页数 * (每页解析时间 + 每页翻译时间)</p>
                        <br />
                        <p>「每页解析时间」取决于当前页面布局复杂程度、当前会员的解析速度</p>
                        <p>
                          「每页翻译时间」取决于当前页面中的内容数量、所选择的翻译模型、当前会员的翻译线路
                        </p>
                        <br />
                        <p>
                          当前为 {userService.vipVersionName}，已自动应用
                          {userService.translateSpeedLabel}，页面解析速度为{' '}
                          {userService.getPlanFileMaxRule('pdf_ultra').fileParseSpeed} 页/分钟
                        </p>
                        <p>注：预计用时仅供参考，实际用时可能快于或慢于预计用时</p>
                      </div>
                    }
                    placement='top'
                    getPopupContainer={(el) => el.parentElement!}
                  >
                    <img
                      className={style.warningIcon}
                      src={commonUtils.getStaticFile('common/icon__warning.png')}
                    />
                  </Popover>
                  ：{estimateTranslateTime(totalPage, userService.vipVersion)}
                </p>
              </div>
              {userService.vipVersion !== 'ultra' && (
                <UpgradeLinkBtn
                  version={userService.vipVersion === 'free' ? 'max' : 'ultra'}
                  feature='pdfPro'
                  source='pdf_pro_middle_status'
                  text={
                    userService.vipVersion === 'free'
                      ? '立即升级，解锁更快翻译速度'
                      : '补差价升级，解锁极速解析'
                  }
                  styleCSS={{ fontSize: '12px', marginTop: 4 }}
                />
              )}
            </div>
          </div>
        </div>

        <div className={classNames(style.right)}>
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('online__source-lang.png')}
              />
              <p className={classNames(style.text)}>原文语言</p>
            </div>

            <div className={classNames(style.setting)}>
              <LanguagePopover
                placement='bottomRight'
                placeholder={'请输入搜索内容'}
                emptyPlaceholder={'不支持当前语言'}
                languageList={userService.sourceLanguageOptions}
                value={userService.pdfProSourceLang}
                onLanguageChange={(value) => {
                  userService.pdfProSourceLang = value
                  userService.savePdfProLanguageInfo(value, userService.pdfProTargetLang)
                }}
                getPopupContainer={(el) => el.parentElement!}
                onLoadChildren={(languageItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{languageItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></LanguagePopover>
            </div>
          </div>

          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('online__source-lang.png')}
              />
              <p className={classNames(style.text)}>目标语言</p>
            </div>

            <div className={classNames(style.setting)}>
              <LanguagePopover
                placement='bottomRight'
                placeholder={'请输入搜索内容'}
                emptyPlaceholder={'不支持当前语言'}
                languageList={userService.targetLanguageOptions}
                value={userService.pdfProTargetLang}
                onLanguageChange={(value) => {
                  userService.pdfProTargetLang = value
                  userService.savePdfProLanguageInfo(userService.pdfProSourceLang, value)
                }}
                getPopupContainer={(el) => el.parentElement!}
                onLoadChildren={(languageItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{languageItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></LanguagePopover>
            </div>
          </div>

          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>翻译模型</p>
            </div>

            <div className={classNames(style.setting)}>
              <ModelPopover
                apiFilter='translate'
                placement='bottomRight'
                currentModel={userService.pdfProTranslateModel}
                onModelChange={(model) => {
                  // 保存用户手动选择的模型偏好
                  userService.savePdfProUserSelectedModel(model)
                  pageChanged(startPage, endPage)
                }}
                onRenderCurrentItem={(item) => {
                  return (
                    <div className={classNames(style.modelItem)}>
                      <img className={classNames(style.modelIcon)} src={item.icon} />
                      <p className={classNames(style.modelName)}>{item.name}</p>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </div>
                  )
                }}
              />
            </div>
          </div>

          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>译文样式</p>
            </div>

            <div className={classNames(style.setting)}>
              <SearchPopover
                placement='bottomRight'
                customStyles={{
                  width: 220,
                }}
                searchEnable={false}
                menuList={TranslateServiceConsts.STYLE_CONFIG_OPTIONS}
                getPopupContainer={(el) => el.parentElement!}
                value={styleDefaultValue}
                onValueChange={(value) => {
                  onStyleChange(value)
                }}
                onRenderItem={(menuItem) => {
                  return <p className={classNames(style.main)}>{menuItem.label}</p>
                }}
                onLoadChildren={(menuItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{menuItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></SearchPopover>
            </div>
          </div>

          {/* 术语表 */}
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <a
                style={{ textDecoration: 'underline' }}
                href='/dashboard/glossary'
                target='__blank'
                className={classNames(style.text)}
              >
                术语表
              </a>
            </div>

            <div className={classNames(style.setting)}>
              <SearchPopover
                footerComponent={
                  <>
                    {userService.vipVersion === 'free' && (
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 10,
                        }}
                      >
                        <UpgradeLinkBtn
                          version='max'
                          text='解锁术语表'
                          source='glossarySettingPopover'
                          feature='glossary'
                        />
                      </div>
                    )}
                  </>
                }
                placement='bottomRight'
                searchEnable={false}
                menuList={TranslateServiceConsts.GLOSSARY_CONFIG_OPTIONS}
                getPopupContainer={(el) => el.parentElement!}
                value={glossaryMode}
                onValueChange={(value) => {
                  if (value === 'on' && userService.vipVersion === 'free') {
                    userService.doPay({
                      from: 'pdf_pro_glossary',
                      version: 'max',
                      feature: 'glossary',
                    })
                    return
                  }
                  onGlossaryChange(value)
                  userService.savePdfProGlossaryMode(value)
                }}
                customStyles={{
                  width: 220,
                }}
                onRenderItem={(menuItem) => {
                  return (
                    <div
                      style={{ display: 'flex', alignItems: 'space-between' }}
                      className={classNames(style.main)}
                    >
                      {menuItem.label}
                      {menuItem.value === 'on' && <VipBadge version='max' />}
                    </div>
                  )
                }}
                onLoadChildren={(menuItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{menuItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></SearchPopover>
            </div>
          </div>

          {/* 文件解析速度 */}
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>
                文件解析速度
                <Popover
                  content={
                    <div style={{ maxWidth: '320px' }}>
                      <p>
                        系统需要一定的服务器资源对 PDF 分段翻译、PDF 无损翻译、Word
                        翻译中上传的文件进行解析
                      </p>
                      <br />
                      <p>我们会为更高级会员提供更多的文件解析资源，以提供更快的文件解析速度</p>
                    </div>
                  }
                  placement='right'
                  getPopupContainer={(el) => el.parentElement!}
                >
                  <img
                    className={style.warningIcon}
                    src={commonUtils.getStaticFile('common/icon__warning.png')}
                  />
                </Popover>
              </p>
            </div>

            <div className={classNames(style.setting)}>
              <SearchPopover
                footerComponent={
                  <>
                    {userService.vipVersion !== 'ultra' && (
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 10,
                        }}
                      >
                        <UpgradeLinkBtn
                          version={userService.vipVersion === 'free' ? 'max' : 'ultra'}
                          text={userService.vipVersion === 'free' ? '解锁高速解析' : '解锁极速解析'}
                          source='pdf_pro_parse'
                          feature='parseSpeed'
                        />
                      </div>
                    )}
                  </>
                }
                placement='bottomRight'
                searchEnable={false}
                menuList={TranslateServiceConsts.PARSE_SPEED_CONFIG_OPTIONS}
                getPopupContainer={(el) => el.parentElement!}
                value={parseMode}
                onValueChange={(value) => {
                  const vipVersion = userService.vipVersion
                  if (vipVersion !== 'ultra' && value === 'high') {
                    userService.doPay({
                      from: 'pdf_pro_parse',
                      version: 'ultra',
                      feature: 'parseSpeed',
                    })
                    return
                  }
                  if (vipVersion === 'free' && value !== 'low') {
                    userService.doPay({
                      from: 'pdf_pro_parse',
                      version: 'max',
                      feature: 'parseSpeed',
                    })
                    return
                  }
                  setParseMode(value)
                }}
                customStyles={{
                  width: 260,
                }}
                onRenderItem={(menuItem) => {
                  return (
                    <div
                      style={{ display: 'flex', alignItems: 'space-between' }}
                      className={classNames(style.main)}
                    >
                      {menuItem.label}
                      {menuItem.value === 'medium' && <VipBadge version='max' />}
                      {menuItem.value === 'high' && <VipBadge version='ultra' />}
                    </div>
                  )
                }}
                onLoadChildren={(menuItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{menuItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></SearchPopover>
            </div>
          </div>

          {/* 智能语境 */}
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>
                智能语境
                <Popover
                  content={
                    <div style={{ maxWidth: '320px' }}>
                      <p>
                        开启后会自动提取 PDF 文件的上下文信息，并根据不同的解析模式，通过
                        AI模型生成对应的语境解析，有效提高翻译质量
                      </p>
                      <p>
                        开启智能语境功能后，翻译接口会携带额外的上下文信息，因此会消耗更多的翻译
                        Token
                      </p>
                    </div>
                  }
                  placement='right'
                  getPopupContainer={(el) => el.parentElement!}
                >
                  <img
                    className={style.warningIcon}
                    src={commonUtils.getStaticFile('common/icon__warning.png')}
                  />
                </Popover>
              </p>
            </div>

            <div className={classNames(style.setting)}>
              <SearchPopover
                footerComponent={
                  <>
                    {userService.vipVersion !== 'ultra' && (
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: 10,
                        }}
                      >
                        <UpgradeLinkBtn
                          version={userService.vipVersion === 'free' ? 'max' : 'ultra'}
                          text={userService.vipVersion === 'free' ? '解锁高级解析' : '解锁深度解析'}
                          source='pdf_pro_context'
                          feature='context'
                        />
                      </div>
                    )}
                  </>
                }
                placement='bottomRight'
                searchEnable={false}
                menuList={TranslateServiceConsts.CONTEXT_TYPE_CONFIG_OPTIONS}
                getPopupContainer={(el) => el.parentElement!}
                value={contextType}
                onValueChange={(value) => {
                  const vipVersion = userService.vipVersion
                  if (vipVersion !== 'ultra' && value === 'full') {
                    userService.doPay({
                      from: 'pdf_pro_context',
                      version: 'ultra',
                      feature: 'context',
                    })
                    return
                  }
                  if (vipVersion === 'free' && value !== 'close') {
                    userService.doPay({
                      from: 'pdf_pro_context',
                      version: 'max',
                      feature: 'context',
                    })
                    return
                  }
                  setContextType(value)
                  userService.savePdfProContextType(value)
                }}
                customStyles={{
                  width: 260,
                }}
                onRenderItem={(menuItem) => {
                  return (
                    <Popover
                      placement='right'
                      key={menuItem.value}
                      content={
                        menuItem.value !== 'close' && (
                          <div style={{ maxWidth: '280px' }}>
                            {menuItem.value === 'deep' && (
                              <p>
                                高级解析：由 AI 生成 3
                                个维度的语境解析。解析程度适中，质量提升适中，速度适中
                              </p>
                            )}
                            {menuItem.value === 'full' && (
                              <p>
                                深度解析：由 AI 生成 8
                                个维度的语境解析。解析程度较深，质量提升较高，速度较慢
                              </p>
                            )}
                          </div>
                        )
                      }
                    >
                      <div
                        style={{ display: 'flex', alignItems: 'space-between' }}
                        className={classNames(style.main)}
                      >
                        {menuItem.label}
                        {menuItem.value === 'deep' && <VipBadge version='max' />}
                        {menuItem.value === 'full' && <VipBadge version='ultra' />}
                      </div>
                    </Popover>
                  )
                }}
                onLoadChildren={(menuItem) => {
                  return (
                    <p className={classNames(style.value)}>
                      <span>{menuItem.label}</span>
                      <img
                        className={classNames(style.arrow)}
                        src={commonUtils.getStaticFile('online__arrow.png')}
                      />
                    </p>
                  )
                }}
              ></SearchPopover>
            </div>
          </div>

          {/* 翻译线路 */}
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>
                翻译线路
                <Popover
                  content={
                    <div style={{ maxWidth: '320px' }}>
                      <p>系统会根据用户当前会员类型，默认为其应用对应的翻译线路</p>
                      <br />
                      <p>基础线路：正常情况满足大多数用户需求，高峰时段限速</p>
                      <p>
                        高级线路：相比基础线路，<strong>翻译速度更快</strong>，高峰时间可能限速
                      </p>
                      <p>
                        优质线路：相比高级线路，翻译速度略快，<strong>全时段不限速</strong>
                      </p>
                    </div>
                  }
                  placement='right'
                  getPopupContainer={(el) => el.parentElement!}
                >
                  <img
                    className={style.warningIcon}
                    src={commonUtils.getStaticFile('common/icon__warning.png')}
                  />
                </Popover>
              </p>
            </div>
            <div className={classNames(style.setting)}>
              <TranslateSpeedSettingPopover
                childrenClassName={classNames(style.value)}
                className={classNames(style.translateSpeedSettingPopover)}
              />
            </div>
          </div>

          {/* 翻译页数 */}
          <div className={classNames(style.item)}>
            <div className={classNames(style.title)}>
              <img
                className={classNames(style.icon)}
                src={commonUtils.getStaticFile('pdf_page.png')}
              />
              <p className={classNames(style.text)}>翻译页数</p>
            </div>

            <div className={classNames(style.inputWrap)}>
              第
              <InputNumber
                type='number'
                // min={1}
                // max={totalPage}
                value={startPage}
                defaultValue={1}
                controls={false}
                precision={0}
                step={1}
                changeOnBlur={true}
                style={{ maxWidth: 56, margin: '0 4px', textAlign: 'center' }}
                onChange={(value) => {
                  setStartPage(value)
                  pageChanged(value, endPage)
                }}
              />
              页 ~ 第
              <InputNumber
                type='number'
                // min={1}
                // max={totalPage}

                value={endPage}
                step={1}
                precision={0}
                defaultValue={totalPage}
                changeOnBlur={true}
                controls={false}
                style={{ maxWidth: 56, margin: '0 4px', textAlign: 'center' }}
                onChange={(value) => {
                  setEndPage(value)
                  pageChanged(startPage, value)
                }}
              />
              页
            </div>

            <div className={classNames(style.info, { [style.error]: error })}>{info}</div>
            {info && (
              <div className={classNames(style.upgradeBtnWrap)}>
                {userService.vipVersion !== 'free' && buyPackageBtnText && (
                  <div
                    className={classNames(style.upgradeBtn)}
                    onClick={() => {
                      userService.doPay({
                        wordPack: true,
                        wordPackType: 'pdf_pro',
                        from: 'pdf_pro_middle_buy_package',
                        feature: 'pdfPro',
                      })
                    }}
                  >
                    {buyPackageBtnText}
                  </div>
                )}
                {upgradeBtnText && (
                  <div
                    className={classNames(style.upgradeBtn)}
                    onClick={() => {
                      userService.doPay({
                        from: 'pdf_pro_no_page',
                        version: userService.vipVersion === 'free' ? 'max' : 'ultra',
                        feature: 'pdfPro',
                      })
                    }}
                  >
                    {upgradeBtnText}
                  </div>
                )}
              </div>
            )}
          </div>

          <div
            className={classNames(style.button, { [style.disabled]: error })}
            onClick={() => {
              translateNow()
            }}
          >
            立即翻译
            <img
              className={classNames(style.arrow)}
              src={commonUtils.getStaticFile('pdf_btn_arrow.png')}
            />
          </div>
        </div>
      </div>
    </div>
  )
})
